import { createAction } from '@reduxjs/toolkit'
import { isEmpty } from 'lodash'
import { all, call, ForkEffect, put, select, takeEvery } from 'redux-saga/effects'

import { getFlagTypeById } from '../utils/getFlagTypeById'
import { generateOpeningLinkForHighlight } from './createFlag'
import { linkGeometryFromFlag } from '../../../api/projects-api'
import { FlagFilters, NewOpeningLink, NormalizedFlag } from '../../../models/flags'
import { DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import { isFlagVisibleBasedOnFilters } from '../../../shared/services/flags/flags-service'
import managers from '../../lib/managers'
import { Flag, Workspace } from '../../lib/toolBoxes/2D'
import { selectActiveHighlightOpeningId, selectDrawableLocations } from '../../slices/2D'
import { ActiveGeometryGroup, selectActiveGeometryGroup } from '../../slices/geometry'
import {
    selectFlagFilters,
    selectMaterialFlagIndexByOpeningId,
    selectMaterialFlags,
    updateFlag,
} from '../../slices/materialFlags'
import { IMUP2DDrawableLocation, Vector2D } from '../../types'

export const linkGeometryFromFlagAction = createAction<{
    flag_id: number
    newOpeningLinks?: NewOpeningLink[]
}>('linkGeometryFromFlag')

export function* handleLinkGeometryFromFlag({
    payload: { flag_id, newOpeningLinks },
}: ReturnType<typeof linkGeometryFromFlagAction>) {
    try {
        const paperManager = yield call(managers.get2DManager)

        if (!paperManager) return

        const workspaceTool: Workspace = yield call(paperManager.getTool, Workspace.NAME)

        const materialFlagIndexByOpeningId: ReturnType<typeof selectMaterialFlagIndexByOpeningId> = yield select(
            selectMaterialFlagIndexByOpeningId
        )

        const flagTool: Flag = yield call(paperManager.getTool, Flag.NAME)
        const flags: NormalizedFlag[] = yield select(selectMaterialFlags)

        const flagToUpdate = flags.find((f) => f.id === flag_id)

        if (!flagToUpdate) return

        const newOpeningLinksToAdd: NewOpeningLink[] = newOpeningLinks || []

        if (!newOpeningLinks) {
            // Fallback to activeDrawableGroup if newOpeningLinks is not provided
            const activeDrawableGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)

            if (activeDrawableGroup?.openings.length) {
                const openingLinksData = activeDrawableGroup.openings
                    .filter((opening) => opening.isActive)
                    .flatMap((opening) =>
                        opening.opening_locations
                            .filter((loc) => !('joist_length' in loc.additional_data))
                            .map((loc) => ({
                                document_chunk_id: loc.document_chunk_id,
                                coordinates: loc.coordinates,
                                opening_id: loc.opening_id,
                            }))
                    )

                for (const openingLinkData of openingLinksData) {
                    const drawableAreaItems: paper.Item[] = yield call(
                        workspaceTool.getItemsWithCriteria,
                        'data',
                        (data) =>
                            data.drawable_id === openingLinkData.opening_id && data.shapeType === DRAWING_TYPES.AREA
                    )

                    const nextIndex = isEmpty(materialFlagIndexByOpeningId?.[openingLinkData?.opening_id])
                        ? 0
                        : Object.keys(materialFlagIndexByOpeningId[openingLinkData.opening_id]).length

                    const drawableCenterCoordinates: Vector2D = yield call(
                        flagTool.getFlagPointFromCoordinates,
                        openingLinkData.coordinates,
                        !!drawableAreaItems.length,
                        nextIndex
                    )

                    if (
                        materialFlagIndexByOpeningId?.[openingLinkData.opening_id]?.[flag_id] === undefined &&
                        !newOpeningLinksToAdd.some((link) => link.opening_id === openingLinkData.opening_id)
                    ) {
                        newOpeningLinksToAdd.push({
                            opening_id: openingLinkData.opening_id,
                            document_chunk_id: openingLinkData.document_chunk_id,
                            coordinates: drawableCenterCoordinates,
                        })
                    }
                }
            } else {
                // No active material -- fall back to the active highlight, if any
                const activeHighlightOpeningId: number | null = yield select(selectActiveHighlightOpeningId)

                if (activeHighlightOpeningId != null) {
                    const drawableLocations: IMUP2DDrawableLocation[] = yield select(selectDrawableLocations)

                    const links: NewOpeningLink[] = yield* generateOpeningLinkForHighlight(
                        activeHighlightOpeningId,
                        flagTool,
                        materialFlagIndexByOpeningId,
                        drawableLocations
                    )

                    newOpeningLinksToAdd.push(...links)
                }
            }

            if (!newOpeningLinksToAdd.length) return
        }

        const newFlagResponse = yield call(linkGeometryFromFlag, flag_id, {
            opening_links: newOpeningLinksToAdd,
        })

        if (newOpeningLinksToAdd?.length) {
            const type = getFlagTypeById(newFlagResponse.type_id)

            const flagFilters: FlagFilters = yield select(selectFlagFilters)

            const openingLinksWithIds = newFlagResponse.opening_links.filter((responseLink) =>
                newOpeningLinksToAdd.some((addedLink) => addedLink.opening_id === responseLink.opening_id)
            )

            // add saved flags to blueprint
            yield all(
                openingLinksWithIds.map((link) =>
                    call(
                        flagTool.drawFlagByCoords,
                        link.coordinates,
                        flagToUpdate.id,
                        link.opening_id,
                        flagToUpdate.order,
                        type,
                        isFlagVisibleBasedOnFilters(
                            flagFilters,
                            type,
                            newFlagResponse.status_id,
                            newFlagResponse?.creationJiraStatus?.id,
                            newFlagResponse.category_id,
                            newFlagResponse.created_by_user_email
                        ),
                        newFlagResponse.status_id,
                        newFlagResponse?.creationJiraStatus?.id,
                        false,
                        link.id,
                        newFlagResponse.category_id,
                        newFlagResponse.created_by_user_email
                    )
                )
            )

            yield put(updateFlag([newFlagResponse]))
        }
    } catch (error) {
        yield call(console.error, error)
    }
}

export function* watchForLinkGeometryFromFlag(): Generator<ForkEffect<never>, void, unknown> {
    yield takeEvery(linkGeometryFromFlagAction.type, handleLinkGeometryFromFlag)
}
